import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { DONUT_COLORS, EASE, OTHER_COLOR, fmtUsd } from "./nl";
import { useEntitySelection } from "./EntitySelectionContext";

/* Chain Distribution — custom radial composition (Figma contract).
   Not a stock pie chart: segmented ring, dominant-share core, synchronized
   legend, cross-module selection sync with the Protocol Network (hover =
   transient local isolate, click = sticky shared selection). */

const R = 66, SW = 24, C = 2 * Math.PI * R;

export const ChainDistribution = ({ overview }: { overview: any }) => {
  const reduced = useReducedMotion();
  const { selectedChain, setSelectedChain } = useEntitySelection();
  const dist = overview.chain_distribution?.chains || [];
  const top = dist.slice(0, 4);
  const otherShare = Math.max(0, 100 - top.reduce((s: number, c: any) => s + (c.share_pct || 0), 0));
  const otherTvl = dist.slice(4).reduce((s: number, c: any) => s + (c.tvl_usd || 0), 0);
  const rows = [...top.map((c: any, i: number) => ({ ...c, color: DONUT_COLORS[i], key: c.chain })),
                ...(otherShare > 0.05 ? [{ chain: "Other deployments", share_pct: Math.round(otherShare * 10) / 10,
                    tvl_usd: otherTvl, color: OTHER_COLOR, key: "__other" }] : [])];

  const selected = selectedChain && top.find((c: any) => c.chain === selectedChain)
    ? selectedChain : null;
  const [hoverChain, setHoverChain] = useState<string | null>(null);
  const active = hoverChain || selected;
  const userActive = !!active;
  const readout = active || top[0]?.chain;
  const selRow = rows.find((r) => r.chain === readout) || rows[0];

  const commit = (c: string) => {
    if (selectedChain === c) {
      setSelectedChain(null);
      setHoverChain(null);
    } else {
      setSelectedChain(c);
    }
  };

  const segments = useMemo(() => {
    let acc = 0;
    return rows.map((r) => {
      const arc = (r.share_pct / 100) * C;
      const gap = Math.min(2.2, arc * 0.18);
      const len = Math.max(0.6, arc - gap);
      const seg = { ...r, dash: `${len} ${C - len}`, offset: -acc };
      acc += arc;
      return seg;
    });
  }, [dist]);

  return (
    <div className="rounded-[22px] bg-[#06060a] border border-[#222234] p-[20px] md:p-[26px] h-full lg:flex lg:flex-col"
      data-testid="chain-distribution">
      <div className="md:max-lg:flex md:max-lg:items-center md:max-lg:gap-[36px] lg:flex-1 lg:flex lg:flex-col lg:justify-evenly">
        <div className="flex justify-center shrink-0">
          <svg viewBox="0 0 220 220" className="w-[170px] h-[170px] md:w-[190px] md:h-[190px] lg:w-[220px] lg:h-[220px]" role="img"
          aria-label={`Chain distribution: ${rows.map((r) => `${r.chain} ${r.share_pct}%`).join(", ")}.`}>
          <circle cx={110} cy={110} r={R} fill="none" stroke="#242437" strokeWidth={SW} />
          {segments.map((s, i) => (
            <motion.circle key={s.key} cx={110} cy={110} r={R} fill="none"
              stroke={s.color} strokeWidth={SW}
              strokeDasharray={s.dash} strokeDashoffset={s.offset}
              transform="rotate(-90 110 110)"
              style={{ cursor: s.key === "__other" ? "default" : "pointer" }}
              onClick={() => s.key !== "__other" && commit(s.chain)}
              onMouseEnter={() => s.key !== "__other" && setHoverChain(s.chain)}
              onMouseLeave={() => setHoverChain(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: !userActive || s.chain === active || s.key === "__other" ? 1 : 0.35 }}
              transition={{ duration: reduced ? 0.1 : 0.25, delay: reduced ? 0 : i * 0.05, ease: EASE }} />
          ))}
          <text x={110} y={116} textAnchor="middle" className="nl-sg" fontSize={30} fontWeight={500}
            fill="#ffffff" letterSpacing="-0.01em" data-testid="distribution-selected-share">
            {`${selRow?.share_pct}%`}
          </text>
          <text x={110} y={138} textAnchor="middle" className="nl-geist" fontSize={9} fontWeight={600}
            letterSpacing="0.72px" fill="#9696a8">{(selRow?.chain || "").toUpperCase()}</text>
          </svg>
        </div>
        <div className="mt-[20px] md:max-lg:mt-0 lg:mt-0 space-y-[6px] md:max-lg:flex-1" role="list">
        {rows.map((r) => {
          const isSel = r.chain === readout;
          return (
            <button key={r.key} role="listitem"
              data-testid={`distribution-row-${r.key === "__other" ? "other" : r.chain.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => r.key !== "__other" && commit(r.chain)}
              onMouseEnter={() => r.key !== "__other" && setHoverChain(r.chain)}
              onMouseLeave={() => setHoverChain(null)}
              className={`w-full flex items-center gap-[12px] rounded-[10px] px-[8px] py-[6px] text-left transition-colors duration-200 ${
                isSel ? "bg-[#0e0e16]" : "hover:bg-[#0a0a10]"}`}>
              <span className="w-[8px] h-[8px] rounded-full shrink-0" style={{ background: r.color }} aria-hidden />
              <span className="nl-geist text-[12px] leading-[18px] font-medium text-white flex-1 truncate">{r.chain}</span>
              <span className="nl-mono text-[11px] leading-[18px] font-medium text-[#b9b9c7] w-[52px] text-right">
                {r.share_pct != null ? `${r.share_pct}%` : "—"}</span>
              <span className="nl-mono text-[11px] leading-[18px] font-medium text-[#9696a8] w-[64px] text-right">
                {fmtUsd(r.tvl_usd)}</span>
            </button>
          );
        })}
        </div>
      </div>
    </div>
  );
};
